import React, { useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import AutomationRuleEditor, {
  type AutomationRuleEditorHandle,
} from '../../components/Automation/AutomationRuleEditor';

type AutomationTemplate = {
  vertical: string;
  key: string;
  name: string;
  description?: string;
  trigger_event: string;
  conditions: any[];
  actions: any[];
};

/**
 * 2026-09-01 (Docs/AUTOMATION_VISUAL_WORKFLOW_BUILDER_PLAN.md): dedicated
 * full-page route for creating/editing a rule, replacing AutomationRuleDialog
 * (a right-side Sheet capped at 92vw) — live feedback was that a capped
 * drawer still reads as "a box," and the Flow canvas (AutomationFlowEditor)
 * genuinely wants real viewport space, not a fraction of it squeezed into a
 * panel.
 *
 * The back-button in the header below is deliberate, not decorative: the
 * 2026-08-09 history note on AutomationRulesPage.tsx records that an
 * earlier embedded-in-page attempt got reverted specifically for lacking a
 * clear way back to "see all rules." This route has one from the start.
 *
 * 2026-09-01 (built from Docs/mockups/automation-rule-editor-canvas.html,
 * live feedback "everything should be in canvas"): Save now lives in THIS
 * header, not a second one inside AutomationRuleEditor — one unified top
 * bar (Back | title | Save), matching the mockup, instead of two stacked
 * headers. AutomationRuleEditor exposes `save()` via a ref
 * (useImperativeHandle) and reports saving/canSave back through
 * onStateChange so this button enables/disables exactly as it did when it
 * lived inside that component.
 *
 * initialTemplate travels via router navigation state (not a URL param) --
 * see AutomationRulesPage.tsx's handleCreateFromTemplate — since it's a
 * full object (conditions/actions arrays), not something that belongs in
 * a URL.
 */
const AutomationRuleEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const initialTemplate = (location.state as { initialTemplate?: AutomationTemplate } | null)?.initialTemplate ?? null;
  const ruleId = id ? Number(id) : null;

  const goBack = () => navigate('/dashboard/automation');

  const editorRef = useRef<AutomationRuleEditorHandle>(null);
  const [saveState, setSaveState] = useState({ saving: false, canSave: false });

  return (
    // 2026-09-01: h-full, not a guessed 100vh-minus-header calc — confirmed
    // DashboardLayout.tsx's own <main> is already `flex-1 overflow-y-auto`
    // inside a height-bounded flex column ("page scroll lives in <main>,
    // so full-bleed..." per that file's own comment), so a direct child
    // filling h-full correctly inherits the real available height instead
    // of this page re-deriving it from a header height that could drift.
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-3 border-b border-border bg-background px-6 py-3.5">
        <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={goBack}>
          <ArrowLeft className="h-4 w-4" />
          Back to Automation Rules
        </Button>
        <span className="h-4 w-px bg-border" />
        <h1 className="text-sm font-semibold tracking-tight">
          {ruleId ? 'Edit Automation Rule' : 'Create Automation Rule'}
        </h1>
        <div className="flex-1" />
        <Button
          type="button"
          size="sm"
          className="gap-2"
          disabled={!saveState.canSave}
          onClick={() => editorRef.current?.save()}
        >
          {saveState.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saveState.saving ? 'Saving…' : ruleId ? 'Update rule' : 'Create rule'}
        </Button>
      </div>
      <div className="h-0 min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <AutomationRuleEditor
          ref={editorRef}
          key={ruleId ?? initialTemplate?.name ?? 'new'}
          ruleId={ruleId}
          initialTemplate={initialTemplate}
          onSaved={goBack}
          onStateChange={setSaveState}
        />
      </div>
    </div>
  );
};

export default AutomationRuleEditorPage;
